import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Grid2x2 as Grid, Play, X, ChevronLeft, ChevronRight, Filter, Maximize2 } from 'lucide-react';
import { chProjectService, chBrandingService } from '../services';
import { chPostService } from '../services/postService';
import { useModal } from '../../contexts/ModalContext';
import type { ChPost, ChBranding } from '../types';
import { PLATFORM_LABELS, CONTENT_TYPE_LABELS } from '../types';
import { MediaPreview } from '../components/MediaPreview';
import { MediaCarousel } from '../components/MediaCarousel';
import { getCoverUrl, getMediaUrls, getPlatforms } from '../lib/posts';

type ViewMode = 'grid' | 'slideshow';

export function PresentationPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { toast } = useModal();
  const [posts, setPosts] = useState<ChPost[]>([]);
  const [branding, setBranding] = useState<ChBranding | null>(null);
  const [projectName, setProjectName] = useState('');
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<ViewMode>('grid');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [onlyPublished, setOnlyPublished] = useState(false);

  useEffect(() => {
    if (!slug) return;
    loadData();
  }, [slug]);

  async function loadData() {
    try {
      const project = await chProjectService.getBySlug(slug!);
      if (!project) { navigate('/contenthub'); return; }
      setProjectName(project.name);

      const [allPosts, brand] = await Promise.all([
        chPostService.getByProject(project.id),
        chBrandingService.get(project.id),
      ]);

      const presentable = allPosts.filter(p => ['aprovado', 'publicado'].includes(p.status));
      setPosts(presentable);
      setBranding(brand);
    } catch {
      toast.error('Erro ao carregar a apresentação');
      navigate('/contenthub');
    } finally {
      setLoading(false);
    }
  }

  const filteredPosts = onlyPublished
    ? posts.filter(p => p.status === 'publicado')
    : posts;

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (mode !== 'slideshow') {
      if (e.key === 'Escape') navigate(-1);
      return;
    }
    if (e.key === 'ArrowRight' || e.key === ' ') {
      setCurrentSlide(prev => Math.min(prev + 1, filteredPosts.length - 1));
    } else if (e.key === 'ArrowLeft') {
      setCurrentSlide(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Escape') {
      setMode('grid');
    }
  }, [mode, filteredPosts.length, navigate]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const primaryColor = branding?.primary_color || '#15140F';
  const bgColor = branding?.background_color || '#0A0A0A';

  function getContrastColor(hex: string): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#0A0A0A' : '#FFFFFF';
  }

  const textColor = getContrastColor(bgColor);
  const subtextColor = textColor === '#FFFFFF' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.5)';

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center" style={{ background: bgColor }}>
        <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  if (mode === 'slideshow') {
    return <SlideshowView
      posts={filteredPosts}
      currentSlide={currentSlide}
      setCurrentSlide={setCurrentSlide}
      bgColor={bgColor}
      textColor={textColor}
      subtextColor={subtextColor}
      primaryColor={primaryColor}
      onExit={() => setMode('grid')}
    />;
  }

  return (
    <div
      className="fixed inset-0 overflow-y-auto"
      style={{ background: bgColor, color: textColor }}
    >
      {/* Toolbar */}
      <div className="sticky top-0 z-20 backdrop-blur-xl" style={{ background: `${bgColor}e6` }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {branding?.logo_url && (
              <img src={branding.logo_url} alt="" className="h-8 object-contain" />
            )}
            <h1 className="text-lg font-semibold tracking-tight">{projectName}</h1>
          </div>

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none" style={{ color: subtextColor }}>
              <Filter className="w-3.5 h-3.5" />
              <span>Apenas publicados</span>
              <input
                type="checkbox"
                checked={onlyPublished}
                onChange={e => setOnlyPublished(e.target.checked)}
                className="w-4 h-4 rounded accent-current"
                style={{ accentColor: primaryColor }}
              />
            </label>

            <div className="w-px h-5" style={{ background: subtextColor }} />

            <button
              onClick={() => { setCurrentSlide(0); setMode('slideshow'); }}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all hover:scale-105"
              style={{ background: primaryColor, color: getContrastColor(primaryColor) }}
            >
              <Play className="w-3.5 h-3.5" />
              Slideshow
            </button>

            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-lg transition-colors hover:bg-white/10"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Grid */}
      {filteredPosts.length === 0 ? (
        <div className="flex flex-col items-center justify-center min-h-[60vh]" style={{ color: subtextColor }}>
          <Maximize2 className="w-12 h-12 mb-4 opacity-40" />
          <p className="text-lg font-medium">Sem posts para apresentar</p>
          <p className="text-sm mt-1">Apenas posts aprovados ou publicados aparecem aqui.</p>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPosts.map((post, idx) => (
              <button
                key={post.id}
                onClick={() => { setCurrentSlide(idx); setMode('slideshow'); }}
                className="group relative rounded-2xl overflow-hidden aspect-[4/5] transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl text-left"
                style={{ background: `${primaryColor}15` }}
              >
                {getCoverUrl(post) ? (
                  <MediaPreview
                    url={getCoverUrl(post)!}
                    alt={post.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center" style={{ background: `${primaryColor}20` }}>
                    <Grid className="w-12 h-12 opacity-20" />
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-5 translate-y-2 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
                  <h3 className="text-white font-semibold text-base leading-tight mb-1">{post.title}</h3>
                  <p className="text-white/60 text-xs">
                    {getPlatforms(post).map(p => PLATFORM_LABELS[p]).join(' + ')} &middot; {CONTENT_TYPE_LABELS[post.content_type]}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SlideshowView({
  posts,
  currentSlide,
  setCurrentSlide,
  bgColor,
  textColor,
  subtextColor,
  primaryColor,
  onExit,
}: {
  posts: ChPost[];
  currentSlide: number;
  setCurrentSlide: (n: number | ((p: number) => number)) => void;
  bgColor: string;
  textColor: string;
  subtextColor: string;
  primaryColor: string;
  onExit: () => void;
}) {
  const post = posts[currentSlide];
  const [touchStart, setTouchStart] = useState<number | null>(null);

  function handleTouchStart(e: React.TouchEvent) {
    setTouchStart(e.touches[0].clientX);
  }

  function handleTouchEnd(e: React.TouchEvent) {
    if (touchStart === null) return;
    const diff = e.changedTouches[0].clientX - touchStart;
    if (Math.abs(diff) > 60) {
      if (diff < 0 && currentSlide < posts.length - 1) {
        setCurrentSlide(currentSlide + 1);
      } else if (diff > 0 && currentSlide > 0) {
        setCurrentSlide(currentSlide - 1);
      }
    }
    setTouchStart(null);
  }

  function getContrastColor(hex: string): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#0A0A0A' : '#FFFFFF';
  }

  if (!post) return null;

  return (
    <div
      className="fixed inset-0 flex flex-col"
      style={{ background: bgColor, color: textColor }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-4 z-10">
        <span className="text-sm font-medium" style={{ color: subtextColor }}>
          {currentSlide + 1} / {posts.length}
        </span>
        <button
          onClick={onExit}
          className="p-2 rounded-lg transition-colors hover:bg-white/10"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center px-6 relative overflow-hidden">
        {/* Prev button */}
        {currentSlide > 0 && (
          <button
            onClick={() => setCurrentSlide(currentSlide - 1)}
            className="absolute left-4 z-10 p-3 rounded-full transition-all hover:scale-110"
            style={{ background: `${primaryColor}30` }}
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
        )}

        {/* Slide */}
        <div className="w-full max-w-4xl mx-auto flex flex-col items-center gap-8 animate-[fadeIn_0.3s_ease-out]">
          {getCoverUrl(post) ? (
            <div className="w-full max-h-[60vh] flex items-center justify-center">
              {getMediaUrls(post).length > 0 ? (
                <MediaCarousel
                  key={post.id}
                  urls={getMediaUrls(post)}
                  alt={post.title}
                  poster={post.thumbnail_url}
                  mediaClassName="max-w-full max-h-[60vh] object-contain rounded-2xl shadow-2xl"
                  accent={primaryColor}
                />
              ) : (
                <img
                  src={post.thumbnail_url || ''}
                  alt={post.title}
                  className="max-w-full max-h-[60vh] object-contain rounded-2xl shadow-2xl"
                />
              )}
            </div>
          ) : (
            <div
              className="w-full aspect-video max-w-2xl rounded-2xl flex items-center justify-center"
              style={{ background: `${primaryColor}15` }}
            >
              <Grid className="w-16 h-16 opacity-20" />
            </div>
          )}

          <div className="text-center max-w-2xl">
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-3">{post.title}</h2>
            {post.caption && (
              <p className="text-base leading-relaxed" style={{ color: subtextColor }}>
                {post.caption.length > 200 ? post.caption.slice(0, 200) + '...' : post.caption}
              </p>
            )}
            <div className="flex items-center justify-center gap-3 mt-4">
              <span
                className="px-3 py-1 rounded-full text-xs font-medium"
                style={{ background: `${primaryColor}20`, color: getContrastColor(bgColor) }}
              >
                {getPlatforms(post).map(p => PLATFORM_LABELS[p]).join(' + ')}
              </span>
              <span
                className="px-3 py-1 rounded-full text-xs font-medium"
                style={{ background: `${primaryColor}10`, color: subtextColor }}
              >
                {CONTENT_TYPE_LABELS[post.content_type]}
              </span>
            </div>
          </div>
        </div>

        {/* Next button */}
        {currentSlide < posts.length - 1 && (
          <button
            onClick={() => setCurrentSlide(currentSlide + 1)}
            className="absolute right-4 z-10 p-3 rounded-full transition-all hover:scale-110"
            style={{ background: `${primaryColor}30` }}
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        )}
      </div>

      {/* Progress dots */}
      <div className="flex items-center justify-center gap-1.5 pb-6">
        {posts.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrentSlide(idx)}
            className="transition-all duration-200"
            style={{
              width: idx === currentSlide ? '24px' : '8px',
              height: '8px',
              borderRadius: '4px',
              background: idx === currentSlide ? primaryColor : `${textColor}30`,
            }}
          />
        ))}
      </div>
    </div>
  );
}
